import { useContext, useEffect, useState } from "react";
import { Table, Button, Card, Switch } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { Line, Column } from "@ant-design/plots";
import axios from "axios";
import { apiUrl } from "../config";
import TableContext from "../Context/TablePosition";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const show = (v) => (v === null || v === undefined || v === "" ? "-" : v);

// Karsilastirma artik frontend'de yapilmiyor: string<->float normalizasyonu ve
// malzeme kodu esdegerligi backend'de cozuldu, satir bazinda `match`, toplamda
// `field_match` olarak geliyor. Burada sadece o bayraklar okunur.
const hasSide = (o) => !!o && Object.keys(o).length > 0;

export default function AIModelComparisonPage() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [onlyUpcoming, setOnlyUpcoming] = useState(true);
  // "Simdi" her render'da yeniden hesaplanmasin diye cekim aninda sabitlenir.
  const [fetchedAt, setFetchedAt] = useState(null);
  const { setCurrentUrl } = useContext(TableContext);
  const location = useLocation();
  const { t } = useTranslation();

  const username = process.env.REACT_APP_USERNAME;
  const password = process.env.REACT_APP_SECRET_KEY;

  useEffect(() => {
    localStorage.setItem("urlpaintshop", location.pathname);
    setCurrentUrl(localStorage.getItem("urlpaintshop"));
  }, [location.pathname, setCurrentUrl]);

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${apiUrl}/getAIModelComparison`, {
        // Satirlar sequence yerine period (zaman dilimi) ile eslestirilsin;
        // aksi halde iki plan farkli siralarda geldigi icin uyum orani hatali cikiyor.
        params: { join_by: "period" },
        auth: { username: username, password: password },
      });
      setResult(res.data || null);
      setFetchedAt(new Date());
    } catch (e) {
      setError(e.message || t("aiComparison.error"));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const allRows = Array.isArray(result?.comparison) ? result.comparison : [];

  // Satirin zamani: eslesmis satirda iki taraf ayni periyodu tasir, tek tarafli
  // satirda dolu olan taraf kullanilir.
  const rowTime = (r) => {
    const d = r.ai?.date ?? r.normal?.date;
    const p = r.ai?.period ?? r.normal?.period;
    if (!d || !p) return null;
    const t = new Date(`${d}T${String(p).slice(0, 8)}`);
    return isNaN(t.getTime()) ? null : t;
  };

  // Gecmis periyotlar uretilmis durumda; ekran operasyonda "bundan sonrasi" icin
  // kullanildigi icin varsayilan olarak simdiki saatten oncesi gizlenir.
  // Zamani okunamayan satir gizlenmez — veri sessizce dusurulmemeli.
  const nowTs = fetchedAt ? fetchedAt.getTime() : Date.now();
  const rows = onlyUpcoming
    ? allRows.filter((r) => {
        const t = rowTime(r);
        return t === null || t.getTime() >= nowTs;
      })
    : allRows;
  const hiddenPastCount = allRows.length - rows.length;

  const AI_COLOR = "#1d4ed8";
  const NORMAL_COLOR = "#6b7280";
  // Uyum tonlari — dogrulanmis (validate_palette: tum kontroller gecti, light yuzey).
  // Alarm kirmizisi yerine yumusak gul; her isaret ayrica yuzde etiketi/kalinlik tasiyor.
  const OK_COLOR = "#2e8b62";
  const MID_COLOR = "#c07b18";
  const DIFF_COLOR = "#b0506f";
  const aiLabel = t("aiComparison.aiGroup");
  const normalLabel = t("aiComparison.normalGroup");

  // AI tarafi duruslari "Planned Stop", normal taraf "Planlı Duruş" olarak yaziyor —
  // ayni sey, iki dilde. Ekranda tek etikete indiriliyor. ("Rework + Duruş" farkli bir
  // durum, dokunulmuyor.) Kalici cozum kaynakta ortak bir kod uretmek.
  const ACTION_ALIASES = {
    "planned stop": "aiComparison.plannedStop",
    "planlı duruş": "aiComparison.plannedStop",
    // JS toLowerCase() Turkce'ye duyarsiz: "PLANLI DURUŞ" -> "planli duruş" (noktali i).
    "planli duruş": "aiComparison.plannedStop",
  };
  const actionLabel = (v) => {
    const key = ACTION_ALIASES[String(v ?? "").trim().toLowerCase()];
    return key ? t(key) : v;
  };

  // 1) Sıra bazlı üretim adedi — iki seri (çizgi)
  const num = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
  const trendData = rows.flatMap((r) => {
    // join_by=period sonrasi tek tarafli satirlarda ai.sequence bos kalabiliyor;
    // eksen once periyoda, sonra siraya duser.
    const s = String(r.ai?.period ?? r.normal?.period ?? r.ai?.sequence ?? r.normal?.sequence ?? "-");
    return [
      { sira: s, model: aiLabel, adet: num(r.ai?.production_qty) },
      { sira: s, model: normalLabel, adet: num(r.normal?.uretim) },
    ];
  });

  // 2) Renk bazlı toplam üretim — gruplu sütun
  const colorMap = {};
  rows.forEach((r) => {
    const c = (r.ai?.color || r.normal?.renk || "-").toString();
    const key = c.toLowerCase();
    if (!colorMap[key]) colorMap[key] = { label: c, ai: 0, normal: 0 };
    colorMap[key].ai += Number(r.ai?.production_qty) || 0;
    colorMap[key].normal += Number(r.normal?.uretim) || 0;
  });
  const colorData = Object.values(colorMap).flatMap((c) => [
    { renk: c.label, model: aiLabel, adet: c.ai },
    { renk: c.label, model: normalLabel, adet: c.normal },
  ]);

  // 3) Uyum oranı — alan bazlı, tamamen backend'in field_match ozetinden.
  // Payda field_match.paired: iki tarafi da dolu satir sayisi. rows.length kullanilamaz,
  // cunku yanit tek tarafli satirlari da iceriyor (AI'da olup normalde olmayan periyot ve tersi).
  const fm = result?.field_match || {};
  const pairedCount = Number(fm.paired) || 0;
  const onlyAi = rows.filter((r) => hasSide(r.ai) && !hasSide(r.normal)).length;
  const onlyNormal = rows.filter((r) => !hasSide(r.ai) && hasSide(r.normal)).length;

  const MATCH_FIELDS = [
    { label: t("aiComparison.color"), key: "color" },
    { label: t("aiComparison.quantity"), key: "qty" },
    { label: t("aiComparison.body"), key: "body" },
    { label: t("aiComparison.frontBody"), key: "front_body" },
  ];

  const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);
  const fieldMatchData = MATCH_FIELDS.map((f) => {
    const eq = Number(fm[f.key]) || 0;
    return { alan: f.label, oran: pct(eq, pairedCount), eq };
  });

  const matched = Number(fm.all) || 0;
  const overallRate = pct(matched, pairedCount);

  // Tek hücrede: üstte AI (mavi), altta Bizim Model (gri).
  // matchFlag backend'den gelir: false ise fark yumusak gul tonuyla isaretlenir
  // (alarm kirmizisi degil); ayrim ayrica kalin yazi tipiyle de tasindigi icin
  // renge bagimli degil. null/undefined ise kiyaslama yok (tek tarafli satir veya
  // date/saat gibi bilgi amacli kolon).
  // titles: hucrede aciklama gosterilirken ham kodu tooltip'te tutmak icin.
  const pair = (aiVal, normalVal, matchFlag, titles) => {
    const diff = matchFlag === false;
    const top = diff ? DIFF_COLOR : "#1d4ed8";
    const bottom = diff ? DIFF_COLOR : "#6b7280";
    return (
      <div className="flex flex-col leading-tight" style={{ fontWeight: diff ? 700 : 400 }}>
        <span style={{ color: top }} title={titles?.[0] ?? undefined}>{show(aiVal)}</span>
        <span
          style={{ color: bottom, borderTop: "1px dashed #d1d5db", marginTop: 2, paddingTop: 2 }}
          title={titles?.[1] ?? undefined}
        >
          {show(normalVal)}
        </span>
      </div>
    );
  };

  const columns = [
    {
      title: t("aiComparison.sequence"),
      key: "sequence",
      align: "center",
      width: 48,
      fixed: "left",
      // Duz satir numarasi. Plan sira alanlari (ai.sequence / normal.sequence) kullanilamaz:
      // ikisi bagimsiz sayaclar ve ikisi de 1'den basliyor, tek hucrede "1 1 2 2" diye okunuyor.
      // Antd'nin render index'i sayfa icindeki sirayi verdigi icin mutlak sira listeden alinir.
      render: (_, r) => rows.indexOf(r) + 1,
    },
    {
      title: t("aiComparison.date"),
      key: "date",
      align: "center",
      width: 120,
      render: (_, r) => pair(r.ai?.date, r.normal?.date),
    },
    {
      title: t("aiComparison.time"),
      key: "time",
      align: "center",
      width: 100,
      render: (_, r) => pair(r.ai?.period, r.normal?.period),
    },
    {
      title: t("aiComparison.action"),
      key: "action",
      align: "center",
      width: 260,
      render: (_, r) => pair(actionLabel(r.ai?.action_name), actionLabel(r.normal?.siparis)),
    },
    {
      title: t("aiComparison.color"),
      key: "color",
      align: "center",
      width: 100,
      render: (_, r) => pair(r.ai?.color, r.normal?.renk, r.match?.color),
    },
    {
      title: t("aiComparison.quantity"),
      key: "quantity",
      align: "center",
      width: 90,
      render: (_, r) => pair(r.ai?.production_qty, r.normal?.uretim, r.match?.qty),
    },
    {
      // Kod yerine aciklama: iki sistemde malzeme kodlari farkli ama ayni urunu
      // gosterebiliyor (ör. 8001287854 / 8001319792 -> "Beyaz U Gövde"). Backend
      // esdegerligi acikama uzerinden cozdugu icin kod gosterilirse tablo, match
      // bayragiyla celisiyormus gibi okunuyor. Ham kod tooltip'te duruyor.
      title: t("aiComparison.body"),
      key: "body",
      align: "center",
      width: 160,
      render: (_, r) =>
        pair(r.ai?.body_material_desc, r.normal?.govde_desc, r.match?.body, [
          r.ai?.body_material_code,
          r.normal?.govde,
        ]),
    },
    {
      title: t("aiComparison.frontBody"),
      key: "frontBody",
      align: "center",
      width: 160,
      render: (_, r) =>
        pair(r.ai?.front_body_material_desc, r.normal?.govde_on_desc, r.match?.front_body, [
          r.ai?.front_body_material_code,
          r.normal?.govde_on,
        ]),
    },
  ];

  return (
    <div className="flex w-full flex-col h-full overflow-y-auto">
      <div className="flex items-center justify-between mt-4 mb-2">
        <div className="text-lg font-semibold">{t("aiComparison.title")}</div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <Switch size="small" checked={onlyUpcoming} onChange={setOnlyUpcoming} />
            {t("aiComparison.onlyUpcoming")}
            {onlyUpcoming && hiddenPastCount > 0 && (
              <span className="text-xs text-gray-400">
                ({hiddenPastCount} {t("aiComparison.pastHidden")})
              </span>
            )}
          </label>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchData}
            loading={loading}
            className="flex items-center justify-center gap-2"
          >
            {t("aiComparison.refresh")}
          </Button>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="flex flex-wrap gap-4 mb-3">
          <Card size="small" title={t("aiComparison.chartTrend")} className="flex-1 min-w-[320px]">
            <Line
              data={trendData}
              xField="sira"
              yField="adet"
              seriesField="model"
              height={220}
              smooth
              connectNulls
              color={[AI_COLOR, NORMAL_COLOR]}
              legend={{ position: "top" }}
              point={{ size: 3 }}
              tooltip={{
                shared: true,
                showCrosshairs: true,
                crosshairs: {
                  type: "x",
                  line: { style: { stroke: "#94a3b8", lineWidth: 1 } },
                },
              }}
              interactions={[{ type: "element-active" }]}
              autoFit={true}
              onReady={(plot) => {
                const invalidate = () => window.dispatchEvent(new Event("resize"));

                // Sürüme göre DOM kapsayıcısını güvenli şekilde bul
                const container =
                  plot?.container ||
                  (typeof plot?.chart?.getContainer === "function"
                    ? plot.chart.getContainer()
                    : null);

                if (container && typeof ResizeObserver !== "undefined") {
                  // Sidebar/menü açılış-kapanışındaki yatay layout kaymasını düzeltir
                  const ro = new ResizeObserver(() => invalidate());
                  ro.observe(container);

                  // Dikey scroll'daki koordinat kaymasını düzeltir
                  const scroller = container.closest?.(".overflow-y-auto");
                  scroller?.addEventListener("scroll", invalidate, { passive: true });

                  if (typeof plot?.chart?.on === "function") {
                    plot.chart.on("destroy", () => {
                      ro.disconnect();
                      scroller?.removeEventListener("scroll", invalidate);
                    });
                  }
                }

                // İlk yükleme sonrası animasyon bitince konumu birkaç kez tazele
                [100, 500, 1000].forEach((d) => setTimeout(invalidate, d));
              }}
            />
          </Card>

          <Card size="small" title={t("aiComparison.chartColor")} className="flex-1 min-w-[320px]">
            <Column
              data={colorData}
              xField="renk"
              yField="adet"
              seriesField="model"
              isGroup
              height={220}
              color={[AI_COLOR, NORMAL_COLOR]}
              legend={{ position: "top" }}
            />
          </Card>

          <Card size="small" title={t("aiComparison.chartMatch")} className="flex-1 min-w-[320px]">
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-2xl font-bold" style={{ color: overallRate >= 50 ? OK_COLOR : DIFF_COLOR }}>
                %{overallRate}
              </span>
              <span className="text-xs text-gray-500">
                {t("aiComparison.allFieldsMatch")} ({matched}/{pairedCount})
              </span>
              {/* Oranlar backend'in field_match ozetinden geliyor ve her zaman gun genelini
                  kapsiyor; tablo saat filtresiyle daraldiginda ikisi ayrisir, bu yuzden
                  kapsam acikca yazilir. */}
              {onlyUpcoming && hiddenPastCount > 0 && (
                <span className="text-xs text-gray-400">· {t("aiComparison.wholeDayScope")}</span>
              )}
            </div>
            <div className="text-xs text-gray-500 mb-2">
              {t("aiComparison.matchedCount")}: {pairedCount} · {t("aiComparison.aiCount")}:{" "}
              {show(result?.ai_count)} · {t("aiComparison.normalCount")}: {show(result?.normal_count)} ·{" "}
              {t("aiComparison.unpairedRows")}: {onlyAi + onlyNormal}
            </div>
            <Column
              data={fieldMatchData}
              xField="alan"
              yField="oran"
              seriesField="alan"
              height={180}
              maxColumnWidth={40}
              yAxis={{ max: 100, label: { formatter: (v) => `%${v}` } }}
              label={{ position: "top", formatter: (d) => `%${d.oran}` }}
              // v1'de tooltip datum'u varsayilan olarak sadece x/y alanlarini tasir;
              // eq alanini gorebilmek icin acikca listelemek gerekiyor.
              tooltip={{
                fields: ["alan", "oran", "eq"],
                formatter: (d) => ({
                  name: t("aiComparison.matchYes"),
                  value: `%${d.oran} (${d.eq}/${pairedCount})`,
                }),
              }}
              // seriesField sirasi data sirasiyla ayni oldugu icin renkler dogrudan eslesiyor.
              color={fieldMatchData.map((d) =>
                d.oran >= 70 ? OK_COLOR : d.oran >= 30 ? MID_COLOR : DIFF_COLOR
              )}
              legend={false}
            />
          </Card>
        </div>
      )}

      {error && <div className="text-red-500 mb-2">{error}</div>}

      <div className="flex flex-col w-full mt-1" style={{ minHeight: 700 }}>
        <Table
          className="ai-outputs-table"
          bordered
          sticky
          loading={loading}
          dataSource={rows}
          // Backend'in `key` alani period join'de dizi, sequence join'de sayi olabiliyor;
          // ikisi karisinca cakisma riski var, satir anahtari indeksten uretiliyor.
          rowKey={(r, i) => `${r.matched_on ?? "none"}-${i}`}
          columns={columns}
          rowClassName={(record, index) =>
            index % 2 === 0
              ? "table-row-light table-height"
              : "table-row-dark table-height"
          }
          scroll={{ x: 1280 }}
          pagination={{ pageSize: 10 }}
        />
      </div>
    </div>
  );
}
